import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  useParticipantIds,
  useScreenShare,
  useDailyEvent,
  useThrottledDailyEvent,
  useLocalSessionId,
  useDaily,
  useMeetingSessionState
} from '@daily-co/daily-react';
import { useAuth } from '../../../contexts/AuthContext';
import { get, post } from '../../../utils/api';
import { isTutorParticipant } from '../participantRoles';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import './Call.css';
import Tile from '../Tile/Tile';
import UserMediaError from '../UserMediaError/UserMediaError';
import LearningSession from '../../LearningSession';
import { InClassroomContext } from '../../../contexts/InClassroomContext';
import TutorLearningSession from '../../TutorView/TutorLearningSession';
import TutorHomePageV2 from '../../TutorView/TutorHomePageV2';
import TutorSingleQuestionPage from '../../TutorView/Quiz/TutorSingleQuestionPage/TutorSingleQuestionPage';
import { routeWhiteboardMessage } from '../../../whiteboard/messageRouter';
import { applyWhiteboardMessageToStore } from '../../../whiteboard/storeUtils';
import { useWhiteboard } from '../../../whiteboard';
import WhiteboardPanel from '../../../whiteboard/WhiteboardPanel';
import LessonOverlayCanvas from '../../../whiteboard/LessonOverlayCanvas';
import ResizableDivider from '../../../whiteboard/ResizableDivider';
import { ScaledLessonContent } from '../../../whiteboard/ScaledLessonContent';
import {
  HAND_RAISED_USER_DATA_KEY,
  useHandRaising
} from '../HandRaising/useHandRaising';
import handRaisedSound from '../../../media/music/hand-raised-effect.mp3';
import { playChatNotificationSound } from '../../../utils/playChatNotificationSound';
import TutorChatPanel from '../../Chat/TutorChatPanel';
import ChatToast from '../../Chat/ChatToast';
import { StudentChatProvider } from '../../Chat/StudentChatContext';
import SparksClassSync from '../../Sparks/SparksClassSync';
//import ProgressBar from '../ProgressBar/ProgressBar';

// Memoize LearningSession component
const MemoizedLearningSession = React.memo(LearningSession);

// Memoized student view component
const StudentView = React.memo(({
  localSessionId,
  tutorParticipantId,
  lessonView,
  callObject,
  registerWhiteboardHandler,
  unregisterWhiteboardHandler,
}) => {
  const whiteboard = useWhiteboard({
    studentId: localSessionId,
    targetParticipantId: tutorParticipantId ?? '',
    localParticipantId: localSessionId,
    callObject: callObject ?? null,
    isTutor: false,
  });

  useEffect(() => {
    if (localSessionId && registerWhiteboardHandler && unregisterWhiteboardHandler) {
      registerWhiteboardHandler(localSessionId, whiteboard.handleReceive);
      return () => unregisterWhiteboardHandler(localSessionId);
    }
  }, [
    localSessionId,
    tutorParticipantId,
    registerWhiteboardHandler,
    unregisterWhiteboardHandler,
    whiteboard.handleReceive,
  ]);

  if (lessonView) {
    const hasTutor = !!tutorParticipantId;
    const showWhiteboard = hasTutor && callObject && registerWhiteboardHandler && whiteboard.whiteboardOpen;
    return (
      <div className={`learning-session-layout ${showWhiteboard ? 'learning-session-layout--with-whiteboard learning-session-layout--whiteboard-mode' : 'learning-session-layout--with-whiteboard'}`}>
        {!hasTutor && (
          <div className="whiteboard-connecting-banner">
            Connecting whiteboard…
          </div>
        )}
        {/* When whiteboard on: 70% main (lesson+whiteboard), 30% bottom strip (tutor tile only) */}
        <div className={showWhiteboard ? 'learning-session learning-session__content learning-session__content--whiteboard-main' : 'learning-session learning-session__content'}>
          <div
            className="learning-session__lesson-area"
            style={{
              flex: showWhiteboard ? 1 - (whiteboard.whiteboardRatio ?? 0.3) : 1,
            }}
          >
            <ScaledLessonContent
              active={showWhiteboard}
              containerRef={whiteboard.lessonContainerRef}
              outerContainerRef={whiteboard.lessonOuterContainerRef}
            >
              <InClassroomContext.Provider value={true}>
                <MemoizedLearningSession
                  studentSessionid={localSessionId}
                />
              </InClassroomContext.Provider>
              {showWhiteboard && (
                <LessonOverlayCanvas
                  lessonCanvasRef={whiteboard.lessonCanvasRef}
                  lessonContainerRef={whiteboard.lessonContainerRef}
                  getPointerHandlers={whiteboard.getPointerHandlers}
                  isActive={whiteboard.isActive}
                  tool={whiteboard.tool}
                  remoteCursor={whiteboard.remoteCursor}
                />
              )}
            </ScaledLessonContent>
          </div>
          {showWhiteboard && (
            <ResizableDivider
              whiteboardRatio={whiteboard.whiteboardRatio ?? 0.3}
              canResize={false}
            />
          )}
          {showWhiteboard && (
            <div
              className="learning-session__whiteboard-area"
              style={{ flex: whiteboard.whiteboardRatio ?? 0.3 }}
            >
              <WhiteboardPanel
                whiteboardCanvasRef={whiteboard.whiteboardCanvasRef}
                whiteboardContainerRef={whiteboard.whiteboardContainerRef}
                getPointerHandlers={whiteboard.getPointerHandlers}
                tool={whiteboard.tool}
                setTool={whiteboard.setTool}
                penColor={whiteboard.penColor}
                setPenColor={whiteboard.setPenColor}
                isActive={whiteboard.isActive}
                setIsActive={whiteboard.setIsActive}
                clearSurface={whiteboard.clearSurface}
                clearAll={whiteboard.clearAll}
                isTutor={false}
                remoteCursor={whiteboard.remoteCursor}
              />
            </div>
          )}
        </div>
        {showWhiteboard && (
          <div className="learning-session__bottom-strip">
            {tutorParticipantId && (
              <div className="learning-session__bottom-strip-tile">
                <Tile key={tutorParticipantId} id={tutorParticipantId} TutorWatchMode />
              </div>
            )}
          </div>
        )}
        {!showWhiteboard && tutorParticipantId && (
          <div className="absolute top-0 right-0">
            <Tile key={tutorParticipantId} id={tutorParticipantId} TutorWatchMode />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="side-by-side-container">
      <Tile id={localSessionId} isLocal={true} isLargeTile={true} />
      {tutorParticipantId && (
        <Tile key={tutorParticipantId} id={tutorParticipantId} isLargeTile={true} />
      )}
    </div>
  );
});

export default function Call({ tutorEmails, onLayoutModeChange, classroomName, lessonScheduleId }) {
  

  /* If a participant runs into a getUserMedia() error, we need to warn them. */
  const [getUserMediaError, setGetUserMediaError] = useState(false);

  const [showLearningSession, setShowLearningSession] = useState(true);

  const  { data }  = useMeetingSessionState();

  const [studentLearningStates, setStudentLearningStates] = useState({});

  // Assessment countdown syncs, keyed by student session id. Kept out of
  // studentLearningStates on purpose: only the timer pill reads this map, so
  // a 30s sync never replaces the mirrored lesson/quiz state of a tile.
  const [studentTimerStates, setStudentTimerStates] = useState({});

  const [studentHearingStates, setStudentHearingStates] = useState({});


  const [tutorParticipant, setTutorParticipant] = useState(null);
  const [studentParticipants, setStudentParticipants] = useState([]);

  const { user, loggedInChild } = useAuth();
  const callObject = useDaily();
  const { handRaisedParticipants } = useHandRaising();
  const handRaiseSoundRef = useRef(null);
  const prevRaisedStateRef = useRef(new Map());
  const studentParticipantsRef = useRef([]);

  useEffect(() => {
    handRaiseSoundRef.current = new Audio(handRaisedSound);

    return () => {
      if (handRaiseSoundRef.current) {
        handRaiseSoundRef.current.pause();
        handRaiseSoundRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    studentParticipantsRef.current = studentParticipants;
  }, [studentParticipants]);

  useEffect(() => {
    const isTutor = tutorEmails?.includes(user?.email);
    if (!isTutor || !callObject) {
      prevRaisedStateRef.current = new Map();
      return;
    }

    const participants = callObject.participants() ?? {};
    const nextRaisedState = new Map();
    let shouldPlaySound = false;

    Object.values(participants).forEach((participant) => {
      if (!participant || participant.local) return;

      const stableParticipantId =
        participant.user_id || participant.user_name || participant.session_id;
      const handRaiseValue = participant.userData?.[HAND_RAISED_USER_DATA_KEY];
      const isRaised = Boolean(handRaiseValue);
      const prevState = prevRaisedStateRef.current.get(stableParticipantId);

      if ((!prevState || !prevState.isRaised) && isRaised) {
        shouldPlaySound = true;
      }

      nextRaisedState.set(stableParticipantId, { isRaised });
    });

    prevRaisedStateRef.current = nextRaisedState;

    if (shouldPlaySound && handRaiseSoundRef.current) {
      handRaiseSoundRef.current.play().catch((err) => {
        console.log('Audio play failed:', err);
      });
    }
  }, [callObject, handRaisedParticipants, tutorEmails, user?.email]);

  //console.log('Call component rendering with participants:', callObject?.participants());
  
  /* We can use the useDailyEvent() hook to listen for daily-js events. Here's a full list
   * of all events: https://docs.daily.co/reference/daily-js/events */
  useDailyEvent(
    'camera-error',
    useCallback(() => {
      setGetUserMediaError(true);
    }, [getUserMediaError]),
  );

  // Listen for ejection events
  useDailyEvent(
    'error',
    useCallback((event) => {
      console.log('Daily error event:', event);
      if (event.error && event.error.type === 'ejected') {
        // Student has been kicked out
        confirmAlert({
          title: 'Removed from Call',
          message: 'You have been removed from the call by the tutor.',
          buttons: [
            {
              label: 'OK',
              onClick: () => {
                // Redirect to home or appropriate page
                window.location.href = '/';
              }
            }
          ],
          closeOnClickOutside: false,
          closeOnEscape: false
        });
      }
    }, []),
  );

  /*useEffect(() => {
    console.log('Student participants state:', studentParticipants);
}, [studentParticipants]);*/

  // Child default policy: join muted and unable to hear tutor until tutor enables it.
  const localStudentCanHearRef = useRef(false);
  const localStudentMicMutedRef = useRef(true);
  const knownStudentIdsRef = useRef(new Set());
  // Tracks the last-seen live-question key per student (keyed by Daily fromId) so we
  // only clear studentInputStates when the student genuinely moves to a new question
  // or lesson step. Previously cleared on every learning-state-update (and every lesson
  // broadcast via `incomingView !== 'quiz'`), which wiped the tutor's live view of
  // in-lesson blank-box typing. If a student reconnects with a new Daily session ID
  // (fromId), their entry here will be absent and input will be cleared once on the
  // first message — correct behaviour.
  const prevLiveQuestionByStudentRef = useRef({});

  // Whiteboard: handlers keyed by studentId (session_id) for routing app messages
  const whiteboardHandlersRef = useRef({});
  // Tutor-only: persist strokes per student so when tutor reopens/refocuses they see same as student
  const tutorWhiteboardStrokesByStudentRef = useRef({});
  const registerWhiteboardHandler = useCallback((studentId, handleReceive) => {
    if (!studentId || typeof handleReceive !== 'function') return;
    whiteboardHandlersRef.current[studentId] = handleReceive;
  }, []);
  const unregisterWhiteboardHandler = useCallback((studentId) => {
    if (!studentId) return;
    delete whiteboardHandlersRef.current[studentId];
  }, []);

  const enforceStudentJoinPolicy = useCallback(() => {
    if (!callObject || tutorEmails.includes(user.email)) return;

    // Always re-apply on join/rejoin so refresh/reconnect can't leak prior media state.
    localStudentCanHearRef.current = false;
    localStudentMicMutedRef.current = true;
    callObject.setLocalAudio(false);

    const participants = callObject.participants();
    Object.values(participants).forEach((participant) => {
      if (participant.local) return;
      if (!isTutorParticipant(participant, tutorEmails)) return;
      callObject.updateParticipant(participant.session_id, {
        setSubscribedTracks: {
          audio: false,
          video: true,
          screenVideo: true,
        },
      });
    });
  }, [callObject, tutorEmails, user.email]);

  // Session ids whose track subscriptions have been set. Subscriptions are applied once per
  // participant session: re-applying them on every roster change used to drop the tutor's
  // audio for every child whenever anyone joined or left. After the first application only
  // the tutor's update-hearing-state message changes a child's subscription to the tutor.
  const subscribedParticipantIdsRef = useRef(new Set());

  const manageTrackSubscriptions = useCallback((participants) => {
    if (!callObject) return;

    const isTutorUser = tutorEmails.includes(user.email);
    const subscribed = subscribedParticipantIdsRef.current;
    const present = new Set();

    Object.values(participants).forEach((participant) => {
      if (participant.local) return;
      present.add(participant.session_id);
      if (subscribed.has(participant.session_id)) return;
      subscribed.add(participant.session_id);

      let tracks;
      if (isTutorUser) {
        tracks = { audio: true, video: true, screenVideo: true };
      } else if (isTutorParticipant(participant, tutorEmails)) {
        tracks = { audio: localStudentCanHearRef.current, video: true, screenVideo: true };
      } else {
        tracks = { audio: false, video: false, screenVideo: false };
      }
      callObject.updateParticipant(participant.session_id, { setSubscribedTracks: tracks });
    });

    subscribed.forEach((sessionId) => {
      if (!present.has(sessionId)) subscribed.delete(sessionId);
    });
  }, [callObject, user.email, tutorEmails]);

  // The tutor list can arrive after participants were first seen (e.g. a refresh mid-call).
  // Forget them so the next pass re-evaluates who is the tutor with the loaded list.
  useEffect(() => {
    subscribedParticipantIdsRef.current = new Set();
  }, [tutorEmails]);

  // THIS BELOW useEffect IS TO PROPERLY HANDLE SUBSCRIPTIONS TO USERS THAT WERE ALREADY IN THE CALL AT THE TIME OF JOINING
  useEffect(() => {
    if (callObject) {
      // Ensure child media policy is applied even if Call mounts after join.
      enforceStudentJoinPolicy();
      handleParticipantUpdate();
    }
  }, [callObject, enforceStudentJoinPolicy]); // Run when callObject is first available

  //useCallback is used to memoize the callback function so that it doesn't get recreated on every render
  //useCallback is a react hook
  const handleParticipantUpdate = useCallback(() => {
    //console.log('handleParticipantUpdate');
    const participants = callObject.participants();

    const tutors = Object.values(participants)
      .filter(participant => isTutorParticipant(participant, tutorEmails));

    // Check for multiple tutors
    if (tutors.length > 1 && tutorEmails.includes(user.email)) {
      // Sort tutors by join time and find the most recent
      const sortedTutors = tutors.sort((a, b) => 
        new Date(b.joined_at) - new Date(a.joined_at)
      );
      
     /* console.log('Sorted tutors by join time:', sortedTutors.map(t => ({
        user_name: t.user_name,
        joined_at: t.joined_at
      })));*/

      const isNewestTutor = sortedTutors[0].user_name === user.email;
      
      //console.log('Current user:', user.email);
      //console.log('Is newest tutor?', isNewestTutor);

      if (isNewestTutor) {
        confirmAlert({
          title: 'Multiple Tutors Detected',
          message: 'There is already a tutor in this call. You will be kicked out.',
          buttons: [
            {
              label: 'OK',
              onClick: async () => {
                try {
                  await callObject.leave();
                } catch (error) {
                  console.error('Error leaving call:', error);
                }
              }
            }
          ],
          closeOnClickOutside: false,
          closeOnEscape: false
        });
      }
    }

    const others = Object.values(participants)
      .filter(participant => !isTutorParticipant(participant, tutorEmails));

    //console.log('tutors', tutors);
    //console.log('others', others);

     // Only set tutor participant if we found one
     // if the tutor joins the call after the student, then that will trigger a re-render on the student's components
     // do I need to deal with this more effectively for the case where the student is doing a quiz in order to prevent null
     // quiz submissions from the student?
     if (tutors.length > 0) {
      // Only set tutor if it's different from current tutor
      if (!tutorParticipant || tutorParticipant.session_id !== tutors[0].session_id) {
        setTutorParticipant(tutors[0]);
      }
    } else if (tutorParticipant) { // Only set to null if we currently have a tutor
      setTutorParticipant(null);
    }

     // only set student participants if the user is a tutor
     // so that we don't trigger re-renders on other students' components
     // I should really probably separate out the student-specific logic from the tutor-specific logic at some point
     if (tutorEmails.includes(user.email)) {
      setStudentParticipants(others);
      const previousStudentIds = knownStudentIdsRef.current;
      const currentStudentIds = new Set(others.map((participant) => participant.session_id));

      others.forEach((studentParticipant) => {
        if (previousStudentIds.has(studentParticipant.session_id)) return;
        callObject.sendAppMessage({
          type: 'update-hearing-state',
          data: { canHear: false },
        }, studentParticipant.session_id);
        callObject.sendAppMessage({
          type: 'update-mic-state',
          data: { isMuted: true },
        }, studentParticipant.session_id);
      });

      knownStudentIdsRef.current = currentStudentIds;
     } else {
      knownStudentIdsRef.current = new Set();
     }

    if ( tutors.length > 0 && !tutorEmails.includes(user.email) ) {
      callObject.sendAppMessage({
        type: 'update-student-hearing-state',
        data: { 
                  canHear: localStudentCanHearRef.current,
                  studentSessionid : localSessionId
              }
      }, tutors[0].session_id);

          // Add this part for mic state sync
      callObject.sendAppMessage({
        type: 'update-student-mic-state',
        data: { 
                  isMuted: localStudentMicMutedRef.current,
                  studentSessionId: localSessionId
              }
      }, tutors[0].session_id);

    };

    // Manage track subscriptions
    manageTrackSubscriptions(participants);

  }, [callObject, manageTrackSubscriptions, enforceStudentJoinPolicy]);


  // see the Daily documentation about useThrottledDailyEvent.
  // it said the callback param has to be a memoized reference otherwise a console error
  // that's why I used useCallback above

  //IMPORTANT: having participant-updated as a dependency caused  the audio subscription on the tutee side to not update properly
  // was it due to having circular depenendencies  of updating the audio subscription?
  useThrottledDailyEvent(
    ['participant-joined', /*'participant-updated',*/ 'participant-left'],
    handleParticipantUpdate, []);

  useDailyEvent(
    'joined-meeting',
    useCallback(() => {
      enforceStudentJoinPolicy();
      handleParticipantUpdate();
    }, [enforceStudentJoinPolicy, handleParticipantUpdate]),
  );




  /* This is for displaying remote participants: this includes other humans, but also screen shares. */
  const { screens } = useScreenShare();
  //const remoteParticipantIds = useParticipantIds({ filter: 'remote' });

  /* This is for displaying our self-view. */
  const localSessionId = useLocalSessionId();
  //const isAlone = remoteParticipantIds.length < 1 || screens.length < 1;



  // Handle incoming app messages
  const [studentInputStates, setStudentInputStates] = useState({});
  const [chatMessagesByStudent, setChatMessagesByStudent] = useState({});
  const [chatUnreadByStudent, setChatUnreadByStudent] = useState({});
  const [chatHistoryCountByStudent, setChatHistoryCountByStudent] = useState({});
  const [chatToast, setChatToast] = useState(null);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const chatPanelOpenRef = useRef(false);
  useEffect(() => { chatPanelOpenRef.current = chatPanelOpen; }, [chatPanelOpen]);
  // Set of mongoChildIds the tutor has explicitly closed via the tile X button
  const [closedChatStudentIds, setClosedChatStudentIds] = useState(new Set());

  const resolveClassroomName = useCallback(() => {
    if (classroomName?.trim()) return classroomName.trim();
    const roomName = callObject?.room?.()?.name;
    if (typeof roomName === 'string' && roomName.trim()) return roomName.trim();
    return null;
  }, [classroomName, callObject]);

  const resolveSessionDate = useCallback(() => {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const appendIncomingChat = useCallback((msg) => {
    const studentId = msg?.childId;
    if (!studentId) return;
    const isTutor = tutorEmails.includes(user?.email);

    setChatMessagesByStudent((prev) => ({
      ...prev,
      [studentId]: [...(prev[studentId] ?? []), msg],
    }));

    if (isTutor && msg.senderType === 'student') {
      const participant = studentParticipantsRef.current.find(
        (p) => String(p?.userData?.mongoChildId || '') === String(studentId),
      );
      const studentName =
        participant?.userData?.displayName ||
        msg.senderDisplayName ||
        participant?.user_name ||
        'Student';

      // Reopen the tile if the tutor had closed it
      setClosedChatStudentIds((prev) => {
        if (!prev.has(studentId)) return prev;
        const next = new Set(prev);
        next.delete(studentId);
        return next;
      });

      setChatUnreadByStudent((prev) => ({
        ...prev,
        [studentId]: (prev[studentId] ?? 0) + 1,
      }));

      if (!chatPanelOpenRef.current) {
        setChatToast({
          studentId,
          studentName,
          message: msg.message,
        });
      }
      playChatNotificationSound();
    }

    if (!isTutor && msg.senderType === 'tutor') {
      setChatUnreadByStudent((prev) => ({
        ...prev,
        [studentId]: (prev[studentId] ?? 0) + 1,
      }));
      setChatToast({
        studentId,
        studentName: 'Tutor',
        message: msg.message,
      });
      playChatNotificationSound();
    }
  }, [tutorEmails, user?.email]);

  const appendIncomingChatRef = useRef(appendIncomingChat);
  useEffect(() => {
    appendIncomingChatRef.current = appendIncomingChat;
  }, [appendIncomingChat]);

  const persistChatMessage = useCallback(async (payload) => {
    try {
      await post(
        '/chat',
        {
          sessionDate: payload.sessionDate,
          childId: payload.childId,
          senderType: payload.senderType,
          message: payload.message,
        },
        true,
      );
    } catch (error) {
      console.error('Failed to persist chat message', error);
    }
  }, []);

  const sendChatMessageAsStudent = useCallback(async (text) => {
    const message = (text ?? '').trim();
    const targetTutorSessionId = tutorParticipant?.session_id;
    const childId = loggedInChild?._id != null ? String(loggedInChild._id) : '';

    if (!message || !callObject || !targetTutorSessionId || !childId) {
      return;
    }

    const payload = {
      type: 'chat-message',
      data: {
        sessionDate: resolveSessionDate(),
        childId,
        senderType: 'student',
        message,
        senderDisplayName: loggedInChild?.name || 'Student',
        timestamp: new Date().toISOString(),
      },
    };

    callObject.sendAppMessage(payload, targetTutorSessionId);
    appendIncomingChat(payload.data);
    await persistChatMessage(payload.data);
  }, [
    appendIncomingChat,
    callObject,
    loggedInChild,
    persistChatMessage,
    resolveSessionDate,
    tutorParticipant?.session_id,
  ]);

  const sendChatMessageAsTutor = useCallback(async (childId, text, targetSessionId) => {
    const message = (text ?? '').trim();

    if (!message || !callObject || !targetSessionId || !childId) {
      return;
    }

    const payload = {
      type: 'chat-message',
      data: {
        sessionDate: resolveSessionDate(),
        childId,
        senderType: 'tutor',
        message,
        timestamp: new Date().toISOString(),
      },
    };

    callObject.sendAppMessage(payload, targetSessionId);
    appendIncomingChat(payload.data);
    await persistChatMessage(payload.data);
  }, [appendIncomingChat, callObject, persistChatMessage, resolveSessionDate]);

  const markStudentOwnChatRead = useCallback(() => {
    const childId = loggedInChild?._id != null ? String(loggedInChild._id) : '';
    if (!childId) return;
    setChatUnreadByStudent((prev) => ({
      ...prev,
      [childId]: 0,
    }));
  }, [loggedInChild]);

  const markStudentChatRead = useCallback((mongoChildId) => {
    if (!mongoChildId) return;
    setChatUnreadByStudent((prev) => ({
      ...prev,
      [mongoChildId]: 0,
    }));
  }, []);

  // Load chat history for a single student (called when panel opens or student first appears)
  const loadChatHistoryForStudent = useCallback((mongoChildId) => {
    if (!mongoChildId) return;
    // Skip if already loaded
    if (chatHistoryCountByStudent[mongoChildId] !== undefined) return;
    const sessionDate = resolveSessionDate();
    get(`/chat/${mongoChildId}/${sessionDate}`, true)
      .then((messages = []) => {
        if (!messages.length) {
          setChatHistoryCountByStudent((prev) => ({ ...prev, [mongoChildId]: 0 }));
          return;
        }
        const normalised = messages.map((msg) => ({
          ...msg,
          childId: mongoChildId,
          timestamp: msg.timestamp ?? msg.createdAt,
        }));
        setChatMessagesByStudent((prev) => ({
          ...prev,
          [mongoChildId]: normalised,
        }));
        setChatHistoryCountByStudent((prev) => ({
          ...prev,
          [mongoChildId]: normalised.length,
        }));
      })
      .catch((error) => {
        console.error('Failed to load chat history for student', mongoChildId, error);
      });
  }, [chatHistoryCountByStudent, resolveSessionDate]);

  // Student side: load own history on mount
  useEffect(() => {
    const isTutor = tutorEmails.includes(user?.email);
    if (isTutor || !data?.LessonView) return;
    const childId = loggedInChild?._id != null ? String(loggedInChild._id) : '';
    if (!childId) return;
    const sessionDate = resolveSessionDate();
    get(`/chat/${childId}/${sessionDate}`, true)
      .then((messages = []) => {
        if (!messages.length) return;
        const normalised = messages.map((msg) => ({
          ...msg,
          childId,
          timestamp: msg.timestamp ?? msg.createdAt,
        }));
        setChatMessagesByStudent((prev) => ({ ...prev, [childId]: normalised }));
        setChatHistoryCountByStudent((prev) => ({ ...prev, [childId]: normalised.length }));
        setChatUnreadByStudent((prev) => ({ ...prev, [childId]: normalised.length }));
      })
      .catch(() => {});
  }, [loggedInChild?._id, data?.LessonView, tutorEmails, user?.email, resolveSessionDate]);

  useDailyEvent(
    'app-message',
    useCallback(({ data, fromId }) => {
      if (!data || typeof data !== 'object') return;
      if (routeWhiteboardMessage(
        { data, fromId },
        whiteboardHandlersRef,
        tutorEmails.includes(user?.email)
          ? (msg) => applyWhiteboardMessageToStore(msg, tutorWhiteboardStrokesByStudentRef, true)
          : undefined
      )) return;

      if (data.type === 'learning-state-update') {

        // Only update the student learning state if the user is a tutor
        // this if statement is crucial otherwise other students' components will re-render
        // because StudentLearningStates is a state variable that all students have

        if ( tutorEmails.includes(user.email) )  {

            setStudentLearningStates(prev => ({
              ...prev,
              [data.data.studentSessionid]: data.data.state
            }));

            // Only clear live input when the student genuinely moves to a new
            // quiz question / explanation, or to a different lesson step.
            const incomingView = data.data.state?.currentView;
            const incomingIndex = data.data.state?.currentQuestionIndex;
            const incomingExplaining = !!data.data.state?.showingExplanation;
            const incomingStep = data.data.state?.currentStep;
            const prev = prevLiveQuestionByStudentRef.current[fromId];

            const questionChanged = !prev
              || incomingView !== prev.view
              || (incomingView === 'quiz'
                && (incomingIndex !== prev.questionIndex
                  || incomingExplaining !== prev.showingExplanation))
              || (incomingView === 'lesson' && incomingStep !== prev.step);

            if (questionChanged) {
              setStudentInputStates(prevInputs => ({
                ...prevInputs,
                [fromId]: null
              }));
            }

            if (incomingView === 'quiz' && incomingIndex != null) {
              prevLiveQuestionByStudentRef.current[fromId] = {
                view: 'quiz',
                questionIndex: incomingIndex,
                showingExplanation: incomingExplaining,
                step: null,
              };
            } else if (incomingView === 'lesson') {
              prevLiveQuestionByStudentRef.current[fromId] = {
                view: 'lesson',
                questionIndex: null,
                showingExplanation: false,
                step: incomingStep,
              };
            } else {
              delete prevLiveQuestionByStudentRef.current[fromId];
            }

        }

      }

      if (data.type === 'assessment-timer-sync') {
        if (tutorEmails.includes(user.email) && data.data?.studentSessionid) {
          setStudentTimerStates(prev => ({
            ...prev,
            [data.data.studentSessionid]: {
              remainingSeconds: data.data.remainingSeconds,
              syncedAt: Date.now(),
            }
          }));
        }
      }

      if (data.type === 'update-hearing-state') {
        // Only the tutor (owner token) may change what a child hears; this subscription is
        // never re-applied, so a message from anyone else would otherwise stick.
        const sender = Object.values(callObject.participants()).find((p) => p.session_id === fromId);
        if (sender?.owner !== true) return;

        // Update audio subscription without triggering re-render
        callObject.updateParticipant(fromId, {
          setSubscribedTracks: {
            audio: data.data.canHear,
            video: true,
            screenVideo: true
          }
        });

        // Update ref instead of state
        localStudentCanHearRef.current = data.data.canHear;

        // Send confirmation back to tutor
        callObject.sendAppMessage({
          type: 'update-student-hearing-state',
          data: { 
            canHear: data.data.canHear,
            studentSessionid: localSessionId
          }
        }, fromId);
      }

    if (data.type === 'update-student-hearing-state') {
      //console.log('Received student hearing update:', data);

      //console.log('Student participants:', studentParticipants);

      //this update-student-hearing-state is only being sent from the student to the tutor,
      // so it shouldn't case a re-render on other students' components
      
      if (tutorEmails.includes(user.email)) {

          setStudentHearingStates(prev => ({
             ...prev,
             [data.data.studentSessionid]: data.data.canHear
           })); 

      }
      //console.log('Final Value ofStudent hearing states:', studentHearingStates);

     // setLocalStudentCanHear(data.data.canHear);  Update the state
    }

    if (data.type === 'update-mic-state') {
      if (!tutorEmails.includes(user.email)) {
        callObject.setLocalAudio(!data.data.isMuted);
        localStudentMicMutedRef.current = data.data.isMuted;
        
        callObject.sendAppMessage({
          type: 'update-student-mic-state',
          data: { 
            isMuted: data.data.isMuted,
            studentSessionId: localSessionId
          }
        }, fromId);
      }
    }

    // Tutor receives confirmation
    if (data.type === 'update-student-mic-state' && tutorEmails.includes(user.email)) {
      setStudentMicStates(prev => ({
        ...prev,
        [data.data.studentSessionId]: data.data.isMuted
      }));
    }

    if (data.type === 'student-input-update') {
      if (tutorEmails.includes(user.email)) {
        setStudentInputStates(prev => ({
          ...prev,
          [data.data.studentSessionid]: {
            questionType: data.data.questionType,
            input: data.data.input,
            isSubmitted: data.data.isSubmitted,
            isCorrect: data.data.isCorrect,
            questionId: data.data.questionId ?? null
          }
        }));
      }

      //console.log('studentInputStates in Call.js', studentInputStates);
    }

    if (data.type === 'chat-message') {
      const msg = data?.data;
      const message = typeof msg?.message === 'string' ? msg.message.trim() : '';
      const childId = typeof msg?.childId === 'string' ? msg.childId.trim() : '';
      const senderType = msg?.senderType;

      if (!message || !childId || (senderType !== 'tutor' && senderType !== 'student')) {
        return;
      }

      appendIncomingChatRef.current({
        ...msg,
        childId,
        message,
        senderType,
        timestamp: msg?.timestamp ?? new Date().toISOString(),
      });
    }

    }, [tutorEmails])
  );

  /*useEffect(() => {
    console.log('Student Learning States:', studentLearningStates);
  }, [studentLearningStates]);*/

  const toggleStudentHearing = useCallback((studentSessionId) => {
 

      //console.log('initial value of student hearing state:', studentHearingStates[studentSessionId]);

      const newState = !studentHearingStates[studentSessionId];
      
      // Send message to the specific student
      callObject.sendAppMessage({
        type: 'update-hearing-state',
        data: { canHear: newState }
      }, studentSessionId);

      /*return {
        ...prev,
        [studentSessionId]: newState
      };*/

      //console.log("this is the room", callObject.room());

   
  }, [callObject, studentHearingStates]);

  // Add state for tracking student mic states
  const [studentMicStates, setStudentMicStates] = useState({});

  // Focus mode: one student takes 70%, others in thumbnail strip
  const [focusedStudentId, setFocusedStudentId] = useState(null);

  // Hover-to-preview: show child's lesson when hovering over thumbnail
  const [hoveredThumbId, setHoveredThumbId] = useState(null);
  const [popoverRect, setPopoverRect] = useState(null);
  const thumbRefs = useRef({});
  const hoverShowTimeoutRef = useRef(null);
  const hoverHideTimeoutRef = useRef(null);
  const HOVER_SHOW_DELAY = 200;
  const HOVER_HIDE_DELAY = 150;

  const handleThumbMouseEnter = useCallback((sessionId) => {
    if (hoverHideTimeoutRef.current) {
      clearTimeout(hoverHideTimeoutRef.current);
      hoverHideTimeoutRef.current = null;
    }
    hoverShowTimeoutRef.current = setTimeout(() => {
      setHoveredThumbId(sessionId);
      hoverShowTimeoutRef.current = null;
    }, HOVER_SHOW_DELAY);
  }, []);

  const handleThumbMouseLeave = useCallback(() => {
    if (hoverShowTimeoutRef.current) {
      clearTimeout(hoverShowTimeoutRef.current);
      hoverShowTimeoutRef.current = null;
    }
    hoverHideTimeoutRef.current = setTimeout(() => {
      setHoveredThumbId(null);
      setPopoverRect(null);
      hoverHideTimeoutRef.current = null;
    }, HOVER_HIDE_DELAY);
  }, []);

  // Update popover position when hovered thumb changes
  useEffect(() => {
    if (!hoveredThumbId) return;
    const el = thumbRefs.current[hoveredThumbId];
    if (el) {
      const rect = el.getBoundingClientRect();
      setPopoverRect({ top: rect.top, left: rect.left, width: rect.width });
    }
  }, [hoveredThumbId]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setFocusedStudentId(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverShowTimeoutRef.current) clearTimeout(hoverShowTimeoutRef.current);
      if (hoverHideTimeoutRef.current) clearTimeout(hoverHideTimeoutRef.current);
    };
  }, []);

  const handleFocusStudent = useCallback((sessionId) => {
    setFocusedStudentId((prev) => (prev === sessionId ? null : sessionId));
  }, []);

  // Simplified toggle function for individual student
  const toggleStudentMic = useCallback((studentSessionId) => {
    const currentState = studentMicStates[studentSessionId] ?? false;
    
    callObject.sendAppMessage({
      type: 'update-mic-state',
      data: { 
        isMuted: !currentState
      }
    }, studentSessionId);
  }, [callObject, studentMicStates]);

  // Individual kick student functionality
  const kickStudent = useCallback((studentSessionId, studentName) => {
    confirmAlert({
      title: 'Kick Student',
      message: `Are you sure you want to kick '${studentName}' out of the call?`,
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              await callObject.updateParticipant(studentSessionId, { eject: true });
              console.log(`Successfully kicked ${studentName}`);
            } catch (error) {
              console.error('Failed to kick student:', error);
              // TODO: Add user-friendly error message
            }
          }
        },
        {
          label: 'Cancel',
          onClick: () => {}
        }
      ],
      closeOnClickOutside: false,
      closeOnEscape: false
    });
  }, [callObject]);



  const isTutorLessonNoFocus = tutorEmails.includes(user.email) && data?.LessonView && !(focusedStudentId && studentParticipants.length >= 1);

  useEffect(() => {
    if (typeof onLayoutModeChange === 'function') {
      onLayoutModeChange(isTutorLessonNoFocus ? 'main-like' : 'viewport');
    }
  }, [isTutorLessonNoFocus, onLayoutModeChange]);

  // All students with a known mongoChildId, minus those the tutor explicitly closed
  const chatStudentList = useMemo(
    () =>
      studentParticipants
        .filter((p) => p.userData?.mongoChildId != null)
        .filter((p) => !closedChatStudentIds.has(String(p.userData.mongoChildId)))
        .map((p) => ({
          studentId: String(p.userData.mongoChildId),
          sessionId: p.session_id,
          studentName: p.userData?.displayName || p.user_name || 'Student',
        })),
    [studentParticipants, closedChatStudentIds],
  );

  // Open the chat panel; clean up closed IDs for students who have left
  const openTutorChat = useCallback(() => {
    const currentMongoIds = new Set(
      studentParticipants
        .map((p) => (p.userData?.mongoChildId != null ? String(p.userData.mongoChildId) : null))
        .filter(Boolean),
    );
    setClosedChatStudentIds((prev) => {
      const next = new Set();
      prev.forEach((id) => { if (currentMongoIds.has(id)) next.add(id); });
      return next;
    });
    // Load history for all visible students that haven't been loaded yet
    currentMongoIds.forEach((id) => {
      if (!closedChatStudentIds.has(id)) loadChatHistoryForStudent(id);
    });
    setChatPanelOpen(true);
  }, [studentParticipants, closedChatStudentIds, loadChatHistoryForStudent]);

  const closeStudentChat = useCallback((mongoChildId) => {
    setClosedChatStudentIds((prev) => new Set([...prev, mongoChildId]));
  }, []);

  const tutorGridContent = tutorEmails.includes(user.email) ? (
    <>
    <div
      className={`tutor-grid ${focusedStudentId && studentParticipants.length >= 1 ? 'tutor-grid--focus' : ''}`}
      style={chatPanelOpen ? { paddingRight: '320px' } : undefined}
    >
          {/* Tutor tile: top when not in focus, in bottom strip when in focus */}
          {localSessionId && !(focusedStudentId && studentParticipants.length >= 1) && (
            <Tile
              id={localSessionId}
              isLocal
            />
          )}

          {/* Student tiles or learning sessions */}
          {data?.LessonView ? (
            // Learning session view (focus mode when 1+ students)
            focusedStudentId && studentParticipants.length >= 1 ? (
              <div className="tutor-focus-layout">
                <div className="tutor-focus-main">
                  {studentParticipants
                    .filter((p) => p.session_id === focusedStudentId)
                    .map((participant) => {
                      const mongoChildId =
                        participant.userData?.mongoChildId != null
                          ? String(participant.userData.mongoChildId)
                          : null;
                      return (
                      <TutorLearningSession
                        key={participant.session_id}
                        studentId={participant.user_id}
                        isTeacherView={true}
                        studentState={studentLearningStates[participant.session_id]}
                        student={participant}
                        canHearTutor={studentHearingStates[participant.session_id] ?? false}
                        onHearingToggle={() => toggleStudentHearing(participant.session_id)}
                        isMicMuted={studentMicStates[participant.session_id] ?? true}
                        onMicToggle={() => toggleStudentMic(participant.session_id)}
                        tutorEmails={tutorEmails}
                        studentInput={studentInputStates[participant.session_id]}
                        timerState={studentTimerStates[participant.session_id]}
                        onKickStudent={() => kickStudent(participant.session_id, participant.userData?.displayName || participant.user_name)}
                        studentName={participant.userData?.displayName || participant.user_name}
                        callObject={callObject}
                        localSessionId={localSessionId}
                        registerWhiteboardHandler={registerWhiteboardHandler}
                        unregisterWhiteboardHandler={unregisterWhiteboardHandler}
                        tutorWhiteboardStoreRef={tutorWhiteboardStrokesByStudentRef}
                        isFocused={true}
                        onFocusClick={() => handleFocusStudent(participant.session_id)}
                        chatMessages={mongoChildId ? (chatMessagesByStudent[mongoChildId] ?? []) : []}
                        chatHistoryCount={mongoChildId ? (chatHistoryCountByStudent[mongoChildId] ?? 0) : 0}
                        onChatSend={
                          mongoChildId
                            ? (text) => sendChatMessageAsTutor(mongoChildId, text, participant.session_id)
                            : undefined
                        }
                        onChatRead={
                          mongoChildId ? () => markStudentChatRead(mongoChildId) : undefined
                        }
                        onChatOpen={mongoChildId ? openTutorChat : undefined}
                      />
                      );
                    })}
                </div>
                <div className="tutor-focus-thumbnails">
                  {localSessionId && (
                    <div className="tutor-focus-thumb tutor-focus-thumb--tutor">
                      <div className="tutor-focus-thumb__video">
                        <Tile id={localSessionId} isLocal />
                      </div>
                    </div>
                  )}
                  {studentParticipants
                    .filter((p) => p.session_id !== focusedStudentId)
                    .map((participant) => {
                      const thumbMongoId =
                        participant.userData?.mongoChildId != null
                          ? String(participant.userData.mongoChildId)
                          : null;
                      const thumbUnread = thumbMongoId
                        ? (chatUnreadByStudent[thumbMongoId] ?? 0)
                        : 0;
                      return (
                      <div
                        key={participant.session_id}
                        ref={(el) => { thumbRefs.current[participant.session_id] = el; }}
                        className="tutor-focus-thumb"
                        onClick={() => handleFocusStudent(participant.session_id)}
                        onMouseEnter={() => handleThumbMouseEnter(participant.session_id)}
                        onMouseLeave={handleThumbMouseLeave}
                      >
                        <div className="tutor-focus-thumb__video">
                          <Tile id={participant.session_id} StudentWatchMode />
                        </div>
                        {thumbUnread > 0 && thumbMongoId && (
                          <button
                            type="button"
                            className="tutor-thumb-chat-badge"
                            onClick={(e) => {
                              e.stopPropagation();
                              setChatPanelActiveStudentId(thumbMongoId);
                              setChatPanelOpen(true);
                              markStudentChatRead(thumbMongoId);
                            }}
                          >
                            💬 {thumbUnread > 9 ? '9+' : thumbUnread}
                          </button>
                        )}
                      </div>
                      );
                    })}
                </div>
                {hoveredThumbId && popoverRect && (() => {
                  const participant = studentParticipants.find((p) => p.session_id === hoveredThumbId);
                  if (!participant) return null;
                  const studentState = studentLearningStates[participant.session_id];
                  const studentInput = studentInputStates[participant.session_id];
                  const currentView = studentState?.currentView;
                  const delayedQuizQuestion = currentView === 'quiz' && studentState?.quizSubmission
                    ? (studentState.showingExplanation && studentState.currentExplanationQuestion
                      ? studentState.currentExplanationQuestion
                      : studentState.quizSubmission.questionAttempts
                        ?.filter(a => a.QuestionStatus === 'not attempted')
                        ?.[0]
                        ?.LessonQuestionId ?? null)
                    : null;
                  const popoverEl = (
                    <div
                      className="tutor-thumb-hover-popover"
                      style={{
                        position: 'fixed',
                        bottom: window.innerHeight - popoverRect.top - 4,
                        left: popoverRect.left,
                      }}
                      onMouseEnter={() => handleThumbMouseEnter(hoveredThumbId)}
                      onMouseLeave={handleThumbMouseLeave}
                    >
                      <div className="tutor-thumb-hover-popover__content">
                        {currentView === 'lesson' && (
                          <TutorHomePageV2
                            studentState={studentState}
                            studentInput={studentInput}
                          />
                        )}
                        {currentView === 'quiz' && studentState?.quizSubmission && (
                          <TutorSingleQuestionPage
                            quizQuestion={delayedQuizQuestion}
                            ButtonsAreClickable={false}
                            onAnswerSelect={() => {}}
                            studentInput={studentInput}
                          />
                        )}
                        {!currentView && (
                          <div className="tutor-thumb-hover-popover__placeholder">
                            Loading lesson…
                          </div>
                        )}
                      </div>
                    </div>
                  );
                  return createPortal(popoverEl, document.body);
                })()}
              </div>
            ) : (
              studentParticipants.map((participant) => {
                const mongoChildId =
                  participant.userData?.mongoChildId != null
                    ? String(participant.userData.mongoChildId)
                    : null;
                return (
                <div key={participant.session_id}>
                  <TutorLearningSession
                    studentId={participant.user_id}
                    isTeacherView={true}
                    studentState={studentLearningStates[participant.session_id]}
                    student={participant}
                    canHearTutor={studentHearingStates[participant.session_id] ?? false}
                    onHearingToggle={() => toggleStudentHearing(participant.session_id)}
                    isMicMuted={studentMicStates[participant.session_id] ?? true}
                    onMicToggle={() => toggleStudentMic(participant.session_id)}
                    tutorEmails={tutorEmails}
                    studentInput={studentInputStates[participant.session_id]}
                    timerState={studentTimerStates[participant.session_id]}
                    onKickStudent={() => kickStudent(participant.session_id, participant.userData?.displayName || participant.user_name)}
                    studentName={participant.userData?.displayName || participant.user_name}
                    callObject={callObject}
                    localSessionId={localSessionId}
                    registerWhiteboardHandler={registerWhiteboardHandler}
                    unregisterWhiteboardHandler={unregisterWhiteboardHandler}
                    tutorWhiteboardStoreRef={tutorWhiteboardStrokesByStudentRef}
                    isFocused={false}
                    onFocusClick={
                      studentParticipants.length >= 1
                        ? () => handleFocusStudent(participant.session_id)
                        : undefined
                    }
                    chatUnreadCount={
                      mongoChildId ? (chatUnreadByStudent[mongoChildId] ?? 0) : 0
                    }
                    onChatOpen={
                      mongoChildId
                        ? openTutorChat
                        : undefined
                    }
                  />
                </div>
                );
              })
            )
          ) : (
            // Regular video tiles view
            studentParticipants.map((participant) => (
              <div key={participant.session_id}>
                <Tile 
                  key={participant.session_id}
                  id={participant.session_id}
                  isLocal={participant.local}
                  lessonView={data?.LessonView}
                  canHearTutor={studentHearingStates[participant.session_id] ?? false}
                  onHearingToggle={() => toggleStudentHearing(participant.session_id)}
                  isMicMuted={studentMicStates[participant.session_id] ?? true}
                  onMicToggle={() => toggleStudentMic(participant.session_id)}
                  tutorEmails={tutorEmails}
                  onKickStudent={() => kickStudent(participant.session_id, participant.userData?.displayName || participant.user_name)}
                  studentName={participant.userData?.displayName || participant.user_name}
                />
              </div>
            ))
          )}

          {/* Screen shares */}
          {screens.map((screen) => (
            <Tile key={screen.screenId} id={screen.session_id} isScreenShare />
          ))}
        </div>

    {tutorEmails.includes(user.email) && data?.LessonView && chatPanelOpen && (
      <TutorChatPanel
        students={chatStudentList}
        messagesByStudent={chatMessagesByStudent}
        unreadByStudent={chatUnreadByStudent}
        historyCountByStudent={chatHistoryCountByStudent}
        onSend={(childId, sessionId, text) => sendChatMessageAsTutor(childId, text, sessionId)}
        onRead={markStudentChatRead}
        onClose={() => setChatPanelOpen(false)}
        onCloseStudent={closeStudentChat}
      />
    )}

    {tutorEmails.includes(user.email) && data?.LessonView && !chatPanelOpen && (
      <ChatToast
        toast={chatToast}
        onDismiss={() => setChatToast(null)}
        onClick={() => {
          setChatToast(null);
          openTutorChat();
        }}
      />
    )}
    </>
  ) : null;

  const studentContent = (
    <div className={`call-viewport__student${data?.LessonView ? ' call-viewport__student--with-lesson' : ''}`}>
      {!tutorEmails.includes(user.email) && (
        (() => {
          const localChildId = loggedInChild?._id != null ? String(loggedInChild._id) : null;
          return (
        <StudentChatProvider
          messages={localChildId ? (chatMessagesByStudent[localChildId] ?? []) : []}
          unreadCount={localChildId ? (chatUnreadByStudent[localChildId] ?? 0) : 0}
          historyCount={localChildId ? (chatHistoryCountByStudent[localChildId] ?? 0) : 0}
          onSend={sendChatMessageAsStudent}
          onRead={markStudentOwnChatRead}
          toast={chatToast}
          onDismissToast={() => setChatToast(null)}
        >
          <SparksClassSync />
          <StudentView
            localSessionId={localSessionId}
            tutorParticipantId={tutorParticipant?.session_id}
            lessonView={data?.LessonView}
            callObject={callObject}
            registerWhiteboardHandler={registerWhiteboardHandler}
            unregisterWhiteboardHandler={unregisterWhiteboardHandler}
          />
        </StudentChatProvider>
          );
        })()
      )}
    </div>
  );

  const renderCallScreen = () => {
    if (isTutorLessonNoFocus) {
      return tutorGridContent;
    }
    return (
      <div className="call-viewport">
        {tutorEmails.includes(user.email) ? tutorGridContent : studentContent}
      </div>
    );
  };

  return getUserMediaError ? <UserMediaError /> : renderCallScreen();
}